import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import CombinedBillCollection from "../../../modules/common/CombinedBillCollection";
import { centralReportingDataActions } from "../../../_actions";

/**
 * Central-module wrapper for the Combined Bill & Collection Actions accordion.
 *
 * It reads the active patient's record from `centralReportingData` (the shape the
 * /central patient-details page uses) and maps it into the normalized `data` shape
 * that <CombinedBillCollection /> expects. This keeps the shared component decoupled
 * from the central Redux slice.
 */
const CentralCombinedBillCollection = () => {
  const dispatch = useDispatch();
  const reportingData = useSelector(
    (state) => state.centralReportingData?.reportingData
  );

  // reportingData can arrive in two shapes depending on which action populated it:
  //   { data: [...] }                (fetched via getCentralReportingData success)
  //   { reportingData: { data: [...] } }  (nested, used in some flows)
  // Handle both so the accordion doesn't intermittently disappear before a hard refresh.
  const row =
    reportingData?.data?.[0] || reportingData?.reportingData?.data?.[0];

  const data = useMemo(() => {
    if (!row) return {};
    return {
      appID: row.appointment_id || row._id || "",
      leadId: row.lead_id || "",
      lead_id: row.lead_id || "",
      collectionStatus: row.collectionStatus || "",
      payment_mode: row.payment_mode || "",
      name: row.patient_name || "",
      patient_name: row.patient_name || "",
      hospital: row.hospitalName?.value || row.hospital || "",
      hospital_name: row.hospitalName?.value || "",
      city: row.city?.value || row.city || "",
      room: row.room || "",
      tabKey: "Final Bill",
      ipdNoOnBill: row.ipdNoOnBill?.value || row.ipdNoOnBill || "",
      finalBillObj: row.finalBillObj || {},
      urlArray: row.urlArray || row.finalBillCentral?.images || [],
      discharge_date: row.discharge_date || "",
      actual_admission_date: row.actual_admission_date || "",
      actualOtStartTime: row.actualOtStartTime || "",
      actualOtEndTime: row.actualOtEndTime || "",
      totalCollectibleAtAdmission: row.totalCollectibleAtAdmission || "",
      totalCollectibleAtDischarge: row.totalCollectibleAtDischarge || "",
      totalCollectedAtAdmission: row.totalCollectedAtAdmission || "",
      totalCollectedAtDischarge: row.totalCollectedAtDischarge || "",
    };
  }, [row]);

  const refreshReporting = () => {
    if (data.appID) {
      dispatch(
        centralReportingDataActions.getCentralReportingData({
          search_query: data.appID,
        })
      );
    }
  };

  // Render as soon as reporting data exists in the store. We intentionally do NOT
  // return null just because `row` isn't resolved yet — that caused the accordion to
  // intermittently disappear until a hard refresh. When `row` is missing we still show
  // the accordion (its body handles the empty/loading state), keeping it consistent
  // with the sibling accordions on the page.
  if (!reportingData) return null;

  return (
    <CombinedBillCollection
      data={data}
      onRefresh={refreshReporting}
      onUpdateIpdNo={() => {}}
      onUpdateInfo={() => {}}
      onClose={() => {}}
    />
  );
};

export default CentralCombinedBillCollection;
